import { baseApi } from '@/stores/api';
import { parseLoginEnvelope } from '@/utils/parseAuthEnvelope';

import type {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  UpdateProfilePayload,
  User,
} from '../types/auth.types';
import { buildProfileFormData } from '../utils/buildProfileFormData';
import {
  mapApiUserToUser,
  mapMeProfileToUser,
  unwrapMeProfilePayload,
  type ApiUserDto,
} from '../utils/mapApiUser';

/** Login body: cùng parser với refresh để không fail unwrap khi envelope/flat khác nhau nhẹ. */
function transformLoginResponse(raw: unknown): LoginResponse {
  const parsed = parseLoginEnvelope(raw);
  if (!parsed) {
    throw new Error('Invalid login response shape');
  }
  return {
    accessToken: parsed.access_token,
    user: mapApiUserToUser(parsed.user as unknown as ApiUserDto),
    refreshToken: null,
    deviceId: parsed.device_id,
  };
}

function transformMeResponse(raw: unknown): User {
  const payload = unwrapMeProfilePayload(raw);
  if (payload) {
    const fromMe = mapMeProfileToUser(payload);
    if (fromMe) return fromMe;
    if (
      typeof payload.id === 'string' &&
      typeof payload.username === 'string' &&
      (typeof payload.role === 'string' || typeof payload.role_slug === 'string')
    ) {
      return mapApiUserToUser(payload as unknown as ApiUserDto);
    }
  }
  if (
    raw &&
    typeof raw === 'object' &&
    'id' in raw &&
    'username' in (raw as object) &&
    ('role' in (raw as object) || 'role_slug' in (raw as object))
  ) {
    return mapApiUserToUser(raw as ApiUserDto);
  }
  throw new Error('Invalid /api/auth/me response shape');
}

export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/api/auth/login',
        method: 'POST',
        data: { username: credentials.username, password: credentials.password },
      }),
      transformResponse: (response: unknown) => transformLoginResponse(response),
      invalidatesTags: ['Auth', 'User', 'Permissions', 'Menu'],
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: '/api/auth/logout',
        method: 'POST',
      }),
      /**
       * Không invalidate Auth/Menu: refetch `/api/auth/me` hoặc `/api/menus/me` sau khi BE đã revoke
       * (hoặc sau `clearTokens` ở client) → 401 và interceptor gọi thêm `/api/auth/refresh`.
       */
      invalidatesTags: [],
    }),
    getCurrentUser: builder.query<User, void>({
      query: () => ({ url: '/api/auth/me' }),
      transformResponse: transformMeResponse,
      providesTags: ['Auth'],
      keepUnusedDataFor: 120,
    }),
    refreshToken: builder.mutation<LoginResponse, void>({
      query: () => ({
        url: '/api/auth/refresh',
        method: 'POST',
      }),
      transformResponse: (response: unknown) => {
        const parsed = parseLoginEnvelope(response);
        if (!parsed) {
          throw new Error('Invalid refresh response');
        }
        return {
          accessToken: parsed.access_token,
          user: mapApiUserToUser(parsed.user as unknown as ApiUserDto),
          refreshToken: null,
          deviceId: parsed.device_id,
        };
      },
    }),
    forgotPassword: builder.mutation<void, ForgotPasswordRequest>({
      query: (body) => ({
        url: '/api/auth/forgot-password',
        method: 'POST',
        data: body,
      }),
    }),
    resetPassword: builder.mutation<void, ResetPasswordRequest>({
      query: (body) => ({
        url: '/api/auth/reset-password',
        method: 'POST',
        data: body,
      }),
    }),
    /* Tạm tắt — dùng GET /api/menus/me để render; khôi phục cùng useEffectiveMenuPermissions (hook đã bỏ gọi API).
    getMyPermissions: builder.query<MenuPermissionsMap, void>({
      query: () => ({ url: '/api/auth/permissions/me' }),
      transformResponse: transformPermissionsResponse,
      providesTags: ['Permissions'],
      keepUnusedDataFor: 120,
    }),
    */
    changePassword: builder.mutation<void, ChangePasswordRequest>({
      query: (body) => ({
        url: '/api/auth/me/password',
        method: 'PUT',
        data: body,
      }),
      invalidatesTags: ['Auth'],
    }),
    verifySession: builder.mutation<void, void>({
      query: () => ({
        url: '/api/auth/verify',
        method: 'POST',
      }),
      transformResponse: () => undefined,
    }),
    updateProfile: builder.mutation<User, UpdateProfilePayload>({
      query: (payload) => ({
        url: '/api/auth/me',
        method: 'PATCH',
        data: buildProfileFormData(payload),
      }),
      transformResponse: (raw: unknown) => transformMeResponse(raw),
      invalidatesTags: ['Auth', 'User'],
    }),
  }),
});

export const {
  useLoginMutation,
  useLogoutMutation,
  useGetCurrentUserQuery,
  useRefreshTokenMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useVerifySessionMutation,
  useUpdateProfileMutation,
} = authApi;
